import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../ui/pagination";
import { Button } from "../ui/button";
import axios from "axios";
import moment from "moment";
import Loader from "../common-components/loader/loader";
import {
  CheckCircle,
  XCircle,
  FileText,
  Edit,
  RotateCcw,
  Upload,
  Trash2,
  CheckCheck,
  Eye,
} from "lucide-react";
import routes from "../../routes/routes";
import { getStatusDisplayLabel } from "../../utils/status-utils";
import StatusBadge from "../common-components/status-badge";

const getStatusIcon = (status) => {
  switch (status) {
    case "Draft":
      return <Edit className="h-4 w-4" />;
    case "Submitted":
      return <Upload className="h-4 w-4" />;
    case "Approved":
      return <FileText className="h-4 w-4" />;
    case "SentToStores":
      return <CheckCheck className="h-4 w-4" />;
    case "Released":
      return <CheckCircle className="h-4 w-4" />;
    case "Rejected":
    case "TakenDown":
      return <XCircle className="h-4 w-4" />;
    case "ReSubmitted":
      return <RotateCcw className="h-4 w-4" />;

    default:
      return null;
  }
};

export default function MyReleases() {
  const [pageLoading, setPageLoading] = useState(true);
  const [releaseList, setReleaseList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const getDraftAlbums = () => axios.get("/albums");

  useEffect(() => {
    setPageLoading(true);
    getDraftAlbums()
      .then((response) => {
        const albums = response.data.data;

        if (albums.length) {
          setReleaseList(albums);
        }
      })
      .finally(() => {
        setPageLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  const deleteAlbum = (album) => {
    toast.info("Deleting album...");
    axios
      .delete("/albums/" + album._id)
      .then(() => {
        toast.success("Album deleted");
        setReleaseList((rl) => rl.filter((r) => r._id !== album._id));
      })
      .catch(() => {
        toast.error("Error deleting album");
      });
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  // Calculate pagination
  const totalPages = Math.ceil(releaseList.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentReleases = releaseList.slice(startIndex, endIndex);

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="space-y-4 bg-background">
        <div>
          <div className="bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px] text-xs uppercase text-muted-foreground font-semibold">
                    Release ID
                  </TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                    Release Title
                  </TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                    Primary Artist
                  </TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                    Release Date
                  </TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-semibold">
                    Status
                  </TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-semibold text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentReleases.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No releases found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentReleases.map((album) => (
                    <TableRow
                      key={album._id}
                      className="border-b hover:bg-muted/30"
                    >
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {album.fingerprint}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={routes.viewRelease + "/" + album._id}
                          className="text-foreground hover:text-primary transition-colors font-medium"
                        >
                          {album.title || (
                            <span className="italic text-muted-foreground">
                              Untitled
                            </span>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell>{album.primaryArtist}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {moment(album.releaseDate).format("MMM Do YYYY")}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={album.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={routes.viewRelease + "/" + album._id}>
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Link>
                          </Button>
                          {(album.status === "Draft" ||
                            album.status === "Rejected") && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteAlbum(album)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  className={
                    currentPage === 1
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className={
                    currentPage === totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </div>
  );
}
